import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Mail, X } from "lucide-react";
import { BrandMark } from "../components/BrandMark";
import { WalletPass } from "../components/WalletPass";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const DEMO = { org_name: "Ora Coffee", pass_title: "Coffee Club", type: "stamp", brand_color: "#12100E", secondary_color: "#C8FF4D", reward: { name: "Free coffee", threshold: 10 } };
const DEMO_STATE = { balance: 7, pass_number: "BP-00042" };

export default function Auth() {
  const { loginInit, loginVerify, resendCode } = useAuth();

  const [step, setStep] = useState("credentials"); // credentials | code
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [showForgot, setShowForgot] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.location.pathname === "/forgot-password";
  });

  const formRef = useRef();

  const startFlow = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await loginInit(email, password);
      setChallengeId(r.challenge_id);
      setStep("code");
      toast.success("Code sent to " + r.email);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const data = await loginVerify(challengeId, code);
      toast.success("Welcome back");
      const hasOrg = data.is_superadmin || (data.organisations && data.organisations.length > 0);
      window.location.assign(data.is_sales && !data.is_superadmin ? "/app/sales" : hasOrg ? "/app" : "/onboarding");
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };

  const doResend = async () => {
    setResending(true);
    try { await resendCode(challengeId); toast.success("New code sent"); setCode(""); }
    catch (err) { toast.error(apiError(err)); }
    finally { setResending(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F4F1E9]">
      {/* left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0B0D10] text-white pt-16 pb-12 px-12 grain relative overflow-hidden">
        <div className="absolute -bottom-20 -right-16 w-[420px] h-[420px] rounded-full bg-[#6474FF]/10 blur-3xl" />
        <Link to="/" className="flex items-center gap-2 relative z-10"><BrandMark size={30} tone="light" /><span className="font-display font-extrabold text-[19px]">BackPocket</span></Link>
        <div className="relative z-10">
          <h2 className="font-display font-extrabold tracking-[-0.03em] text-[2.6rem] leading-[1.02]">
            Let's put your business in their <span className="font-serif-editorial italic font-normal tracking-normal">back pocket.</span>
          </h2>
          <p className="mt-4 text-white/55 max-w-sm text-[15px]">Loyalty that lives in their Wallet. No app, no card, no fuss.</p>
          <div className="mt-10"><WalletPass program={DEMO} wallet="apple" state={DEMO_STATE} /></div>
        </div>
        <div className="text-white/35 text-[13px] relative z-10">© 2026 BackPocket</div>
      </div>

      {/* form */}
      <div className="flex items-center justify-center p-6 sm:p-10 pt-12 sm:pt-14">
        <div className="w-full max-w-[380px]">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-14"><BrandMark size={28} /><span className="font-display font-extrabold text-[18px]">BackPocket</span></Link>

          <AnimatePresence mode="wait">
            {step === "credentials" ? (
              <motion.form key="creds" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}
                onSubmit={startFlow} className="w-full" data-testid="auth-form">
                <h1 className="font-display font-extrabold tracking-[-0.02em] text-[2rem]">Welcome back</h1>
                <p className="text-[#3a3d44]/80 mt-2 mb-6 text-[15px]">We'll send a one-time code to keep your account safe.</p>

                <div className="mb-4"><Label className="text-[13px]">Email</Label><Input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-11 bg-white rounded-xl" placeholder="you@yourshop.com" /></div>
                <div className="mb-6">
                  <div className="flex items-center justify-between"><Label className="text-[13px]">Password</Label><button type="button" onClick={() => setShowForgot(true)} data-testid="auth-forgot-open" className="text-[12px] text-[#3a3d44]/70 hover:text-[#0B0D10]">Forgot?</button></div>
                  <Input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5 h-11 bg-white rounded-xl" placeholder="••••••••" />
                </div>
                <button data-testid="auth-submit" disabled={busy} className="btn-lift w-full bg-[#C8FF4D] text-[#0B0D10] rounded-full h-12 font-semibold text-[15px] flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? "Sending code…" : <>Continue with email <ArrowRight size={16} /></>}
                </button>
                <p className="text-[13px] text-center mt-7 text-[#3a3d44]/70 leading-relaxed" data-testid="auth-concierge-note">
                  Accounts are set up by the BackPocket team.<br />
                  Want one for your business? <Link to="/" className="font-semibold text-[#0B0D10] link-underline">Book a call</Link>
                </p>
              </motion.form>
            ) : (
              <motion.form key="code" ref={formRef} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}
                onSubmit={submitCode} className="w-full" data-testid="auth-code-form">
                <button type="button" onClick={() => { setStep("credentials"); setCode(""); }} className="flex items-center gap-1.5 text-[12.5px] text-[#3a3d44]/70 hover:text-[#0B0D10] mb-5" data-testid="auth-code-back">
                  <ArrowLeft size={13} /> Change email
                </button>
                <div className="h-14 w-14 rounded-2xl bg-[#0B0D10] grid place-items-center mb-5">
                  <Mail size={22} className="text-[#C8FF4D]" strokeWidth={2} />
                </div>
                <h1 className="font-display font-extrabold tracking-[-0.02em] text-[2rem] leading-tight">Check your email</h1>
                <p className="text-[#3a3d44]/80 mt-2 mb-6 text-[14.5px] leading-relaxed">We sent a 6-digit code to <span className="text-[#0B0D10] font-semibold">{email}</span>. It expires in 10 minutes.</p>

                <CodeInput value={code} onChange={setCode} onComplete={(v) => { setCode(v); if (v.length === 6) setTimeout(() => formRef.current?.requestSubmit?.(), 40); }} />

                <button data-testid="auth-code-submit" disabled={busy || code.length !== 6} className="btn-lift w-full mt-6 bg-[#C8FF4D] text-[#0B0D10] rounded-full h-12 font-semibold text-[15px] flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? "Verifying…" : <>Log in <ArrowRight size={16} /></>}
                </button>

                <div className="text-center text-[13px] text-[#3a3d44]/70 mt-5">
                  Didn't get it?{" "}
                  <button type="button" onClick={doResend} disabled={resending} data-testid="auth-code-resend" className="font-semibold text-[#0B0D10] hover:underline disabled:opacity-50">
                    {resending ? "Sending…" : "Resend code"}
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>

      <ForgotPasswordModal open={showForgot} defaultEmail={email} onClose={() => setShowForgot(false)} />
    </div>
  );
}

function ForgotPasswordModal({ open, defaultEmail, onClose }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => { if (open) { setEmail(defaultEmail || ""); setSent(false); } }, [open, defaultEmail]);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#0B0D10]/45 backdrop-blur-sm grid place-items-center p-4" data-testid="forgot-modal" onClick={onClose}>
      <div className="w-full max-w-[400px] bg-white rounded-3xl border border-[#E8E5DE] p-7 relative shadow-[0_40px_80px_-30px_rgba(11,13,16,0.5)]" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} data-testid="forgot-close" className="absolute top-4 right-4 h-8 w-8 rounded-full grid place-items-center hover:bg-[#F4F1E9] text-[#3a3d44]"><X size={16} /></button>
        {sent ? (
          <div className="pt-2 text-center">
            <div className="h-12 w-12 rounded-2xl bg-[#C8FF4D] grid place-items-center mx-auto mb-4"><Mail size={20} className="text-[#0B0D10]" strokeWidth={2} /></div>
            <h2 className="font-display font-extrabold text-[1.4rem] tracking-[-0.01em]">Check your email</h2>
            <p className="text-[#3a3d44]/75 mt-2 text-[13.5px] leading-relaxed">If <span className="text-[#0B0D10] font-semibold">{email}</span> matches an account, you'll get a reset link within a minute.</p>
            <button type="button" onClick={onClose} data-testid="forgot-close-done" className="mt-6 w-full bg-[#0B0D10] text-white rounded-full h-11 font-semibold text-[14px]">Got it</button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-form">
            <div className="h-12 w-12 rounded-2xl bg-[#0B0D10] grid place-items-center mb-4"><Mail size={20} className="text-[#C8FF4D]" strokeWidth={2} /></div>
            <h2 className="font-display font-extrabold text-[1.4rem] tracking-[-0.01em]">Reset your password</h2>
            <p className="text-[#3a3d44]/75 mt-2 mb-5 text-[13.5px] leading-relaxed">Enter your email. We'll send you a link to choose a new password.</p>
            <Label className="text-[13px]">Email</Label>
            <Input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 bg-[#F4F1E9] rounded-xl mb-4" placeholder="you@yourshop.com" />
            <button data-testid="forgot-submit" disabled={busy || !email} className="btn-lift w-full bg-[#C8FF4D] text-[#0B0D10] rounded-full h-11 font-semibold text-[14px] flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? "Sending…" : <>Send reset link <ArrowRight size={15} /></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* --- 6-digit code input with per-cell focus + paste support --- */
function CodeInput({ value, onChange, onComplete }) {
  const refs = useRef([]);
  useEffect(() => { refs.current[0]?.focus(); }, []);
  const chars = value.padEnd(6, " ").split("").slice(0, 6);

  const setDigit = (i, ch) => {
    const filtered = ch.replace(/\D/g, "").slice(0, 1);
    const arr = value.split("");
    arr[i] = filtered;
    const next = arr.join("").slice(0, 6).replace(/\s/g, "");
    onChange(next);
    if (filtered && i < 5) refs.current[i + 1]?.focus();
    if (next.length === 6) onComplete?.(next);
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !chars[i].trim() && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) refs.current[i + 1]?.focus();
  };

  const onPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    onChange(txt);
    refs.current[Math.min(txt.length, 5)]?.focus();
    if (txt.length === 6) onComplete?.(txt);
  };

  return (
    <div className="grid grid-cols-6 gap-2 w-full" data-testid="auth-code-input">
      {chars.map((c, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={c.trim()}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={onPaste}
          data-testid={`auth-code-digit-${i}`}
          className="w-full min-w-0 h-14 rounded-xl bg-white border border-[#E4E0D6] text-center font-display font-bold text-[24px] tracking-[0.02em] tabular-nums focus:border-[#0B0D10] focus:ring-2 focus:ring-[#0B0D10]/10 outline-none transition-all"
        />
      ))}
    </div>
  );
}
